import {
  addEffect,
  addStateHandlers,
  addWrapper,
  flowMax,
  SimpleUnchangedProps,
} from 'ad-hok'
import { getContextHelpers, toObjectKeys } from 'ad-hok-utils'
import { TFunction } from 'i18next'
import {
  Options as AnalyticsOptions,
  trackEvent,
} from 'shared/src/utils/analytics'
import { typedAs } from 'shared/src/utils/typedAs'

import { makeRoundupUrl } from 'utils/routes'
import { ConstrainWidth } from '../../utils/layout'
import { Notification as NotificationComponent } from './Notification'

const TOP_NOTIFICATION_TIMEOUT_MS = 3000
const BOTTOM_NOTIFICATION_TIMEOUT_MS = 8000

export type AnalyticsEventParams = Record<string, any>

interface AnalyticsEvent {
  options: AnalyticsOptions
  params?: AnalyticsEventParams
}

interface NotificationButton {
  title: string
  navigateTo: string
  state?: any
  external?: boolean
  onClick?: () => void
  onClickAnalyticsEvent?: AnalyticsEvent
}

export interface Notification {
  text: string
  button?: NotificationButton
  style: 'top' | 'bottom'
  sticky?: boolean
  onTimeOutAnalyticsEvent?: AnalyticsEvent
}

export const createAddedToRoundupNotification = (
  t: TFunction
): Notification => ({
  text: t('notifications.addedToRoundup'),
  button: {
    title: t('notifications.seeRoundup'),
    navigateTo: makeRoundupUrl(),
    state: { showBackButton: true },
  },
  style: 'top',
  sticky: false,
})

export const createAddedToMyCreatorsNotification = (
  t: TFunction
): Notification => ({
  text: t('notifications.addedToYourCreators'),
  button: {
    title: t('notifications.seeFollowing'),
    navigateTo: '/following',
  },
  style: 'top',
  sticky: false,
})

interface NotificationContext {
  showNotification: (notification: Notification) => void
}

export const [addNotificationContextProviderInternal, addNotificationContext] =
  getContextHelpers<NotificationContext>(toObjectKeys(['showNotification']))

export const addNotificationContextProvider: SimpleUnchangedProps = flowMax(
  addStateHandlers(
    { notification: typedAs<Notification | null>(null) },
    {
      showNotification: () => (notification: Notification) => ({
        notification,
      }),
      clearNotification: () => () => ({
        notification: null,
      }),
    }
  ),
  addEffect(
    ({ notification, clearNotification }) =>
      () => {
        if (!notification) {
          return
        }

        const timer = notification.sticky
          ? setTimeout(() => {}, 0)
          : setTimeout(
              () => {
                if (notification.onTimeOutAnalyticsEvent) {
                  trackEvent(
                    notification.onTimeOutAnalyticsEvent.options,
                    notification.onTimeOutAnalyticsEvent.params
                  )
                }

                clearNotification()
              },
              notification.style === 'top'
                ? TOP_NOTIFICATION_TIMEOUT_MS
                : BOTTOM_NOTIFICATION_TIMEOUT_MS
            )

        return () => {
          clearInterval(timer)
        }
      },
    ['notification']
  ),
  addWrapper((render, { notification, clearNotification }) => (
    <>
      {notification && (
        <ConstrainWidth>
          <NotificationComponent
            notification={notification}
            onAction={clearNotification}
          />
        </ConstrainWidth>
      )}
      {render()}
    </>
  )),
  addNotificationContextProviderInternal
)
